import { destinationShape } from './track-login-destination';

/**
 * Who sends a signed-in worker to `/login` in the middle of the interview.
 *
 * Observation only: nothing here changes where anybody goes.
 *
 * Why it exists. Since the session re-check in `AuthGuard` (30/09/2026), PostHog still shows
 * interview sessions that hop interview -> `/login?redirect_url=<interview>` -> interview 0.3-2 s
 * after a message is sent: 2 of 53 sending sessions on the first day. The session is valid, no
 * `/accounts/me` is called, and `AuthGuard` logs no `auth_guard_redirect`, so the navigation is
 * not the guard's. A replay rules out the phone's Back button (the keyboard is still open when
 * the hop starts). What is left is a client navigation whose caller nobody has seen. This records
 * it: how it was started (`push`, `replace` or `popstate`), from where, at which history position,
 * and the top of the caller's stack.
 *
 * Nothing personal is logged: paths go through `destinationShape` (ids become `:id`), the query
 * is reduced to whether a destination was carried, and the stack keeps only script file names and
 * positions.
 *
 *   SELECT kind, from_path, count(*) FROM "_rumlog" WHERE message = 'login_navigation' GROUP BY kind, from_path
 */
export type LoginNavigationKind =
  | 'push'
  | 'replace'
  /** A history move nobody in the page asked for: the browser's Back/Forward, or a gesture. */
  | 'popstate'
  /** A history move asked for in code: `history.back()`, `go()` or `forward()`, with the caller's stack. */
  | 'history_back'
  | 'history_go'
  | 'history_forward'
  /** Started neither by `push`/`replace` nor by a history move: a navigation Next makes on its own. */
  | 'unknown';

export interface LoginNavigationEvent {
  kind: LoginNavigationKind;
  fromPath: string;
  toPath: string;
  hasRedirectUrl: boolean;
  historyIndex: number | null;
  stack: string;
}

/** The part of the Next router this needs, so a test can hand in a fake. */
export interface TraceableRouter {
  asPath: string;
  push: (...args: any[]) => Promise<boolean>;
  replace: (...args: any[]) => Promise<boolean>;
  events: {
    on: (event: 'routeChangeStart', handler: (url: string) => void) => void;
    off: (event: 'routeChangeStart', handler: (url: string) => void) => void;
  };
}

const LOGIN_PATH = /^(?:\/[a-z]{2})?\/login\/?$/;

const urlOf = (target: unknown): string => {
  if (typeof target === 'string') return target;
  if (target && typeof target === 'object') return (target as { pathname?: string }).pathname ?? '';
  return '';
};

const pathOf = (url: string) => url.split(/[?#]/, 1)[0];
const isLoginUrl = (url: string) => LOGIN_PATH.test(pathOf(url));
const carriesRedirect = (url: string) => /[?&]redirect_url=/.test(url) || /[?&]redirectTo=/.test(url);

/**
 * The top of the caller's stack, script names and positions only: `chunk.js:1:2345`. Minified, but
 * it maps back through the source maps of the build, and it carries no page data.
 */
export const callerStack = (stack: string | undefined, frames = 8): string =>
  (stack ?? '')
    .split('\n')
    .slice(1)
    .map((line) => {
      const match = line.match(/([^/\s()]+\.(?:js|mjs|ts|tsx)(?:\?[^:\s)]*)?):(\d+):(\d+)/);
      return match ? `${match[1].split('?')[0]}:${match[2]}:${match[3]}` : '';
    })
    .filter(Boolean)
    .slice(0, frames)
    .join(' < ');

const historyIndex = (): number | null => {
  try {
    const idx = (window.history.state as { idx?: unknown } | null)?.idx;
    return typeof idx === 'number' ? idx : null;
  } catch {
    return null;
  }
};

/**
 * The last move through the browser history, and who asked for it.
 *
 * Why this is separate and installed at module scope. The first day of `login_navigation`
 * (01/10/2026) reported the interview -> `/login` hops as `unknown`: they do not go through
 * `push`/`replace`, and the `popstate` listener added by `installLoginNavigationTrace` (from a
 * `useEffect`) runs after Next's own, which emits `routeChangeStart` first. A capture-phase
 * listener does not help: both sit on `window`, the event's own target, where only registration
 * order counts. So this must be registered before Next creates its router. In the pages router
 * `_app` is evaluated before `createRouter` (next/dist/client/index.js: `_app` entrypoint at line
 * 599, `createRouter` at 689), which is why `_app` calls `installHistoryProbe()` at module scope.
 *
 * `history.back/go/forward` are wrapped as well: a replay shows the worker is not pressing Back
 * when the hop happens (the keyboard is still open), so something in the page may be calling
 * them, and this records its stack.
 */
type HistoryMoveVia = 'popstate' | 'back' | 'go' | 'forward';
interface HistoryMove {
  at: number;
  via: HistoryMoveVia;
  stack: string;
  fromPath: string;
  idx: number | null;
}

let lastHistoryMove: HistoryMove | null = null;
let historyProbeInstalled = false;

const HISTORY_MOVE_WINDOW_MS = 2000;

/**
 * The path the page is on, kept by watching `pushState`/`replaceState` and every `popstate`.
 * At `popstate` time `location` already shows where the move went, so the page being left can
 * only come from here (review of #451, confirmed on the first production reading).
 */
let currentPath: string | null = null;

const recordHistoryMove = (via: HistoryMoveVia, stack: string, fromPath = window.location.pathname) => {
  lastHistoryMove = { at: Date.now(), via, stack, fromPath, idx: historyIndex() };
};

/** Idempotent; a no-op on the server. Never changes what the wrapped methods do. */
export function installHistoryProbe(): void {
  if (typeof window === 'undefined' || historyProbeInstalled) return;
  historyProbeInstalled = true;
  try {
    const { history } = window;
    (['back', 'go', 'forward'] as const).forEach((method) => {
      const original = history[method] as (...args: unknown[]) => void;
      history[method] = function wrapped(this: History, ...args: unknown[]) {
        try {
          recordHistoryMove(method, callerStack(new Error().stack));
        } catch {
          // Observation must never break a navigation.
        }
        return original.apply(this, args);
      } as History[typeof method];
    });
    currentPath = window.location.pathname;
    (['pushState', 'replaceState'] as const).forEach((method) => {
      const original = history[method] as (...args: unknown[]) => void;
      history[method] = function wrapped(this: History, ...args: unknown[]) {
        const result = original.apply(this, args);
        try {
          currentPath = window.location.pathname;
        } catch {
          // Observation must never break a navigation.
        }
        return result;
      } as History[typeof method];
    });
    window.addEventListener('popstate', () => {
      const leftPath = currentPath ?? window.location.pathname;
      currentPath = window.location.pathname;
      // A `popstate` that follows a programmatic call is that call's; keep its stack.
      const recentCall =
        lastHistoryMove &&
        lastHistoryMove.via !== 'popstate' &&
        Date.now() - lastHistoryMove.at < HISTORY_MOVE_WINDOW_MS;
      if (!recentCall) recordHistoryMove('popstate', '', leftPath);
    });
  } catch {
    // A browser that refuses the wrap loses the observation, not the navigation.
  }
}

/** The history move that started the route change now beginning, if any; consumed once read. */
export function takeRecentHistoryMove(now = Date.now()): HistoryMove | null {
  const move = lastHistoryMove;
  lastHistoryMove = null;
  return move && now - move.at < HISTORY_MOVE_WINDOW_MS ? move : null;
}

const KIND_OF_MOVE: Record<HistoryMoveVia, LoginNavigationKind> = {
  popstate: 'popstate',
  back: 'history_back',
  go: 'history_go',
  forward: 'history_forward',
};

/**
 * Wraps `push` and `replace` on the router instance and listens to `popstate`, then reports every
 * route change whose destination is `/login`. Returns the function that undoes all of it.
 *
 * The method wrappers mark `push` and `replace`. A history move comes from `installHistoryProbe`,
 * which `_app` installs before Next's router so it sees every `popstate` first. Anything else is
 * `unknown`, never guessed as `push`.
 */
export function installLoginNavigationTrace(
  router: TraceableRouter,
  report: (event: LoginNavigationEvent) => void
): () => void {
  let pending: { kind: LoginNavigationKind; stack: string; fromPath: string; idx: number | null } | null = null;

  const originalPush = router.push;
  const originalReplace = router.replace;

  const wrap =
    (kind: 'push' | 'replace', original: TraceableRouter['push']) =>
    (...args: any[]) => {
      // `as` is what the address bar shows; fall back to `url`.
      const target = urlOf(args[1] ?? args[0]);
      if (isLoginUrl(target)) {
        pending = { kind, stack: callerStack(new Error().stack), fromPath: router.asPath, idx: historyIndex() };
      }
      return original.apply(router, args);
    };

  router.push = wrap('push', originalPush);
  router.replace = wrap('replace', originalReplace);

  // Normally already installed at module scope by `_app`; this is the late fallback.
  installHistoryProbe();

  const onRouteChangeStart = (url: string) => {
    const move = takeRecentHistoryMove();
    if (!isLoginUrl(url)) {
      pending = null;
      return;
    }
    // A push/replace wrapper sets `pending` synchronously before Next starts the change; otherwise
    // the history move recorded by the probe in the last two seconds is the origin.
    const fromHistory =
      !pending && move
        ? { kind: KIND_OF_MOVE[move.via], stack: move.stack, fromPath: move.fromPath, idx: move.idx }
        : null;
    const origin = pending ?? fromHistory;
    pending = null;
    try {
      report({
        kind: origin?.kind ?? 'unknown',
        fromPath: destinationShape(origin?.fromPath ?? router.asPath),
        toPath: destinationShape(url),
        hasRedirectUrl: carriesRedirect(url),
        historyIndex: origin?.idx ?? historyIndex(),
        stack: origin?.stack ?? '',
      });
    } catch {
      // Observation must never break a navigation.
    }
  };

  router.events.on('routeChangeStart', onRouteChangeStart);

  return () => {
    router.push = originalPush;
    router.replace = originalReplace;
    router.events.off('routeChangeStart', onRouteChangeStart);
  };
}

/** Fire-and-forget on the O2 logs stream, like `login_destination`. Never throws. */
export function trackLoginNavigation(event: LoginNavigationEvent): void {
  if (typeof window === 'undefined') return;
  import('@openobserve/browser-logs')
    .then(({ openobserveLogs }) => {
      openobserveLogs.logger.info('login_navigation', {
        event: 'login_navigation',
        kind: event.kind,
        from_path: event.fromPath,
        to_path: event.toPath,
        has_redirect_url: event.hasRedirectUrl,
        history_index: event.historyIndex,
        stack: event.stack,
      });
    })
    .catch(() => {
      // Swallow: an uninitialised SDK must never surface to the user.
    });
}
